<script setup lang="ts">
import {
  computed,
  ComputedRef,
  onBeforeMount,
  onMounted,
  onUnmounted,
  reactive,
  Ref,
  ref,
  toRefs,
  unref,
  watch,
  watchEffect,
} from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers, required, requiredIf, maxValue } from '@vuelidate/validators';
import { Frequency, Options, RRule } from 'rrule';
import Divider from 'primevue/divider';
import Calendar from '@/components/Calendar/Calendar.vue';
import InputNumber from 'primevue/inputnumber';
import FileUpload, { FileUploadUploaderEvent } from 'primevue/fileupload';
import MultiSelect from 'primevue/multiselect';
import Textarea from 'primevue/textarea';
import RadioButton from 'primevue/radiobutton';
import AppSelectButton from '@/components/AppSelectButton/AppSelectButton.vue';
import CalendarOverlappingEvents from '@/components/CalendarOverlappingEvents';
import CalendarWarning from './components/CalendarWarning.vue';
import GroupSessionTypeSelect from './components/GroupSessionTypeSelect.vue';
import EventEvaluationAssessmentsFiles from '../EventEvaluationAssessmentsFiles.vue';
import SelectAttendees from '@/components/MultiSelect/SelectAttendees';
import WeekdayPicker from './components/WeekdayPicker.vue';
import ServiceDateError from './components/ServiceDateError.vue';
import {
  DISCIPLINE_ATTENDEE_REQUIRED_EVENT_TYPE,
  EMIT,
  EVENT_TYPE,
  ABRIDGED_FREQUENCY_INTERVALS,
  TENANT_SETTINGS,
} from '@/enum';
import {
  useAuthStore,
  useCalendarStore,
  useEventsModalsStore,
  useServicesStore,
  useTherapyStore,
  useClinicianOrganizationStore,
  useDocumentsStore,
} from '@/store';
import {
  useDateTime,
  useGetDropdownOptions,
  useGetInitialRRuleObject,
  useGetValuesForRecurrenceDropdown,
  useTenant,
  usePersonTypes,
} from '@/composables';
import type {
  AssessmentData,
  AttendeeDropdownOptionInterface,
  AttendeeSessionTypesData,
  DetailedEventInterface,
  DropdownOptionInterface,
  EventCreationFormInterface,
  ClinicianOrganizationInterface,
  RecurrenceDropdownOption,
} from '@/types';
import { storeToRefs } from 'pinia';
import { CONTRACT_BILLING_TYPE } from '@/enum/contract';
import { DropdownChangeEvent } from 'primevue/dropdown';

interface Props {
  eventToEdit?: DetailedEventInterface;
  eventToEditSelected: boolean;
}

const emit = defineEmits([EMIT.SAVE_NEW_EVENT, EMIT.CLOSE_MODAL, EMIT.SAVE_EVENT_FAILED, EMIT.DOCUMENT_EVENT]);
const props = defineProps<Props>();
const authStore = useAuthStore();
const therapyStore = useTherapyStore();
const serviceStore = useServicesStore();
const documentsStore = useDocumentsStore();
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const clinicianOrganizationStore = useClinicianOrganizationStore();
const $tenant = useTenant();

const timezoneChanged = ref<boolean>(false);
const eventDataSet = ref<boolean>(false);
const caseloadOrganization = ref<ClinicianOrganizationInterface | null>();
const { filteredMessage, isAttendeesSelectedFromDashboard, preSelectedAttendees, preselectedEventType } =
  storeToRefs(eventsModalsStore);

const { eventToEdit, eventToEditSelected } = toRefs(props);
const hostId = computed(() => eventToEdit.value?.host_id || (authStore.user?.id as number));
const orgId = computed(
  () => eventToEdit.value?.attendees[0]?.organizations[0]?.id || (authStore.organizationId as number),
);
const saving = ref<boolean>(false);
const startCalendarModel = ref<Date>(new Date());
const endCalendarModel = ref<Date>(new Date());
const startTimeElement = ref();
const endTimeElement = ref();
const showEvaluationAssessmentsFiles = ref<boolean>(false);
const showWarning = ref<boolean>(false);
const overlappingError = ref<boolean>(false);
const warningMessage = ref<string>();
const warningDetails = ref<string[]>([]);
const showCMWarning = ref<boolean>(false);
const cmWarningMessage = ref<string>();
const eventSessionTypes = ref<DropdownOptionInterface[]>([]);
const serviceProviderOrganizations = ref<ClinicianOrganizationInterface[]>([]);
const filteredServiceProviderOrganizations = computed(() =>
  serviceProviderOrganizations.value.filter((org) => !serviceStore.evalOnlyOrganizations.includes(org.id)),
);
const eventEvaluationTypes = ref<DropdownOptionInterface[]>([]);
const evaluationAssessmentsTypesRequest = ref<AssessmentData[]>([]);
const caseloadManagementTypes = ref<DropdownOptionInterface[]>([]);
const selectedAttendees = ref<AttendeeDropdownOptionInterface[]>([]);
const evaluationAssessments: Ref<DropdownOptionInterface[]> = ref([]);
const eventEvaluationAssessment = ref();
const selectedDisciplineId = ref<number>();
const allowedDateRange = ref({ start: new Date(), end: new Date() });
const originalAllowedDateRange = ref({ start: new Date(), end: new Date() });
const v$scope = 'event_modal_validation_scope';
const serviceDateErrorKey = 'service_date_error_component';
const disciplineAttendeeRequiredEventTypes = [...Object.values(DISCIPLINE_ATTENDEE_REQUIRED_EVENT_TYPE)];
const disciplineOnlyRequiredEventTypes = [EVENT_TYPE.IEP_MEETING, EVENT_TYPE.CONSULTATION, EVENT_TYPE.RECORD_REVIEW];
const frequencyIntervals = ref<{ label: ABRIDGED_FREQUENCY_INTERVALS; value: Frequency }[]>([
  { label: ABRIDGED_FREQUENCY_INTERVALS.WEEK, value: RRule.WEEKLY },
  { label: ABRIDGED_FREQUENCY_INTERVALS.MONTH, value: RRule.MONTHLY },
]);
const eventTypes = ref<{ id: number; label: EVENT_TYPE }[]>([]);
const confirmEventOpen = ref(false);
const editEventConditions = ref<'this' | 'future'>('this');
const isSplitCaseload = ref<boolean>(false);
const dateUtil = useDateTime();
const sessionTypesTooltip = `Select the session type for each student. Options include: RE (Regular), AC (Approved compensation), ESY (Extended School Year) and MU (Make-up)`;
const uploadFileEventTypes = [
  EVENT_TYPE.IEP_MEETING,
  EVENT_TYPE.CONSULTATION,
  EVENT_TYPE.RECORD_REVIEW,
  EVENT_TYPE.RESPONSE_TO_INTERVENTION,
];
const evaluationEditAttendees = ref([]);
const { initialRRule } = useGetInitialRRuleObject(
  eventToEdit?.value as DetailedEventInterface,
  eventToEditSelected.value,
);

const eventRRule = ref<Partial<RRule>>(initialRRule.value);

const hostTimezone = computed<string | undefined>(
  () => eventToEdit?.value?.host_timezone_name || authStore?.user?.timezone?.name,
);

const hostTimezoneAbbr = computed<string | undefined>(
  () => eventToEdit?.value?.host_timezone_abbr || authStore?.user?.timezone?.abbr,
);

const { recurrenceOptions, initialSelectedOption } = useGetValuesForRecurrenceDropdown(
  eventRRule as Ref<RRule>,
  eventToEditSelected.value,
);

const changeRRuleTimeZone = () => {
  if (eventRRule.value.options && hostTimezone.value) {
    if (!timezoneChanged.value) {
      const displayStart = useDateTime().formatTimeByTimezone(
        eventToEdit?.value?.start_at as string,
        hostTimezone.value as string,
        true,
      );

      const displayEnd = useDateTime().formatTimeByTimezone(
        eventToEdit?.value?.end_at as string,
        hostTimezone.value as string,
        true,
      );

      eventRRule.value.options.dtstart = new Date(displayStart);
      eventRRule.value.options.until = new Date(displayEnd);
      timezoneChanged.value = true;
    }
  }
};

if (eventToEditSelected.value) {
  startCalendarModel.value = new Date(eventToEdit?.value?.start_at as string);
  endCalendarModel.value = new Date(eventToEdit?.value?.end_at as string);
  if (eventToEdit?.value?.start_at && hostTimezone.value) {
    changeRRuleTimeZone();
  }
} else {
  startCalendarModel.value = eventsModalsStore.preSelectedEventStartAt
    ? new Date(eventsModalsStore.preSelectedEventStartAt as Date)
    : new Date();
  if (eventsModalsStore.preSelectedEventStartAt) {
    (eventRRule.value.options as RRule['options']).dtstart = startCalendarModel.value;
  }
  endCalendarModel.value = eventsModalsStore.preSelectedEventStartAt
    ? new Date(eventsModalsStore.preSelectedEventStartAt as Date)
    : new Date();
  endCalendarModel.value.setMinutes(endCalendarModel.value.getMinutes() + 30);
  if (eventsModalsStore.preSelectedEventStartAt) {
    (eventRRule.value.options as RRule['options']).until = endCalendarModel.value;
    eventsModalsStore.preSelectedEventStartAt = undefined;
  }
}

// This ref is where the recurrence dropdown info is stored. It gets updated anytime the eventRRule
// ref has its start or end date altered
const rRuleRef: Ref<{ id: number; value: Partial<RRule>; label: string } | undefined> = ref(initialSelectedOption);

const isCustomRecurrence: ComputedRef<boolean> = computed(() => rRuleRef?.value?.id === 5);
const isRecurring: ComputedRef<boolean> = computed(() => (rRuleRef?.value?.id as number) > 0);
const loading = computed(
  () => calendarStore.loading || eventsModalsStore.loading || saving.value || !eventTypes.value.length,
);

const isElumaClinician = computed(() => authStore.currentInstanceIsMasterTenant && usePersonTypes.isClinician());

onBeforeMount(async () => {
  await serviceStore.getClinicianServices(hostId.value);
  await serviceStore.getOrganizationServices(orgId.value);
  eventTypes.value = [...Object.values(EVENT_TYPE)].map((value, index) => {
    return { id: index, label: value };
  });
  setOneYearAllowedDateRange();

  eventTypes.value = eventTypes.value.filter((eventType) => isAvailableService(eventType.label as string));

  if (isAttendeesSelectedFromDashboard.value) {
    await getServiceEventToBeSelected();

    await checkIfEventMatchesWithStudent(preselectedEventType.value as EVENT_TYPE);

    eventsModalsStore.loading = false;
  }

  newEvent.therapy_event_type =
    (preselectedEventType.value as EVENT_TYPE) ?? (eventTypes.value[0]?.label || EVENT_TYPE.SESSION);

  if (eventsModalsStore.preSelectedDueAt) {
    newEvent.due_at = eventsModalsStore.preSelectedDueAt;
  }

  const caseloadRequest = await therapyStore.getCaseloadManagementTypes();
  const sessionTypeRequest = await therapyStore.getSessionEventTypes();
  serviceProviderOrganizations.value = await clinicianOrganizationStore.getOrganizationsForClinician(hostId.value);

  setCaseloadOrganization();

  caseloadManagementTypes.value = caseloadRequest.map(({ id, name }) => {
    return {
      name: name as string,
      value: id,
    };
  });
  eventSessionTypes.value = sessionTypeRequest
    .map(({ id, name, acronym }) => {
      return {
        name: name as string,
        value: id,
        acronym,
      };
    })
    .sort((a, b) => a.value - b.value);

  if (therapyStore.allowedDateRange?.contract_windows.length) {
    const allTypes = therapyStore.allowedDateRange.contract_windows.flatMap((window) =>
      window.evaluation_types.map(({ id, title }) => ({
        name: title as string,
        value: id,
      })),
    );

    eventEvaluationTypes.value = [...new Map(allTypes.map((item) => [item.value, item])).values()];
  }

  evaluationAssessmentsTypesRequest.value = await therapyStore.getEvaluationAssessments();
  setAssessments();

  if (eventToEditSelected.value) {
    showEvaluationAssessmentsFiles.value = !!newEvent.assessment_record_form_id;
  } else {
    newEvent.session_types = [getTherapySessionTypeObject(0, eventSessionTypes.value[0].value)];

    newEvent.therapy_evaluation_type_id = therapyStore.eventEvaluations?.find(
      (evaluationType) => evaluationType.name === 'Regular',
    )?.value;
  }
});

/**
 * preset the dropdown selected org if the SP has ONE org OR
 * if there is an event being edited find the right Org connected to the event and populate it
 * if its a new event do not populate it
 */
const setCaseloadOrganization = () => {
  if (serviceProviderOrganizations.value.length === 1)
    caseloadOrganization.value = serviceProviderOrganizations.value[0];
  else if (eventToEdit.value) {
    const organization = eventToEdit?.value.attendees[0].organizations[0];
    caseloadOrganization.value = serviceProviderOrganizations.value.find((org) => {
      return org.id === organization.id;
    });
  }
};

/**
 *  loop through all the clinicians windows and match to allowed date ranges
 *  to find the latest date that matches for all eLuma tenant org contracts for events
 *  note: right now this is only for IEP meetings and could change in the future
 */
const setAllowedTherapyEventElumaContractRange = () => {
  let start = new Date();
  let end = new Date();
  const clinicianWindows = therapyStore.allowedDateRange?.clinician_windows;

  if (clinicianWindows?.length) {
    clinicianWindows.forEach((clinicianWindow) => {
      const [endYear, endMonth, endDay] = clinicianWindow.end_at.split('-').map(Number);
      const [startYear, startMonth, startDay] = clinicianWindow.start_at.split('-').map(Number);

      const newEnd = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      const newStart = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);

      if (newEnd > end) {
        end = newEnd;
        allowedDateRange.value.end = newEnd;
      }
      if (newStart < start) {
        start = newStart;
        allowedDateRange.value.start = newStart;
      }
    });

    verifySelectedDateIsWithinRange();
  }
};

/**
 * if the use selects IEP after selecting a date outside of the allowed contract params
 * then reset the selected date to today
 */
const verifySelectedDateIsWithinRange = () => {
  if (
    startCalendarModel.value < allowedDateRange.value.start ||
    startCalendarModel.value > allowedDateRange.value.end
  ) {
    startCalendarModel.value = new Date();
    endCalendarModel.value = new Date();
  }
};

/**
 * the default allowed date range for event creation === one year back and forward
 */
const setOneYearAllowedDateRange = () => {
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();

  let prevMonth = month === 0 ? 11 : month;
  let prevYear = prevMonth === 11 ? year - 2 : year - 1;
  let nextMonth = month === 11 ? 0 : month + 1;
  let nextYear = nextMonth === 0 ? year + 2 : year + 1;
  originalAllowedDateRange.value = {
    start: new Date(prevYear, prevMonth, 1),
    end: new Date(nextYear, nextMonth, 0),
  };
  resetOneYearAllowedDateRange();
};

const resetOneYearAllowedDateRange = () => (allowedDateRange.value = { ...originalAllowedDateRange.value });

const isAvailableService = (label: string) => {
  return serviceStore.clinicianServices?.find((service) => service.name === label);
};

const selectedEventType: ComputedRef<{ id: number; label: EVENT_TYPE }> = computed(() => {
  const selected = eventTypes.value.find(
    (eventType) => (newEvent.therapy_event_type as EVENT_TYPE) === eventType.label,
  );
  return selected || eventTypes.value[0];
});

const { availableAttendees, availableDisciplines } = await useGetDropdownOptions(
  eventToEdit.value?.host_id || (authStore.user?.id as number),
  selectedDisciplineId as Ref<number>,
  selectedAttendees,
  selectedEventType,
  caseloadOrganization as Ref<ClinicianOrganizationInterface>,
  endCalendarModel,
);

watch(availableAttendees, () => {
  if (unref(eventToEditSelected) && availableAttendees.value.length && !eventDataSet.value) {
    eventsModalsStore.setAttendees(unref(availableAttendees));
    const event = unref(eventToEdit);
    setUpdatedEventData(event as DetailedEventInterface);
    eventDataSet.value = true;
  }
});

const selectedEventTypeIs = (eventType: EVENT_TYPE[]) => {
  return eventType.includes(selectedEventType.value?.label);
};

const getTherapySessionTypeObject = (personId: number, therapySessionTypeId: number) => {
  return { person_id: personId, therapy_session_type: { id: therapySessionTypeId } };
};

const duration = computed(() => {
  const startHour = startTimeElement.value?.currentHour <= 11 ? startTimeElement.value?.currentHour : 0;
  const startMinutes = startTimeElement.value?.currentMinute;
  const endHour = endTimeElement.value?.currentHour <= 11 ? endTimeElement.value?.currentHour : 0;
  const endMinutes = endTimeElement.value?.currentMinute;
  let diff = endHour * 60 + endMinutes - (startHour * 60 + startMinutes);

  if (startTimeElement.value?.pm === endTimeElement.value?.pm) {
    return diff || 0;
  } else {
    diff = (endHour + 12) * 60 + endMinutes - (startHour * 60 + startMinutes);

    return diff || 0;
  }
});

const newEvent: EventCreationFormInterface = reactive({
  rrule: eventRRule as Partial<RRule>,
  attendees: selectedAttendees,
  discipline_id: selectedDisciplineId.value as number,
  duration: duration,
  therapy_event_type: preselectedEventType.value ?? EVENT_TYPE.CASELOAD_MANAGEMENT,
  description: undefined,
  assessment_record_form_id: undefined,
  due_at: undefined,
  documents: [],
  session_types: [getTherapySessionTypeObject(0, 1)],
  therapy_evaluation_type_id: eventToEdit?.value?.event_type_data.therapy_evaluation_type_id
    ? eventToEdit?.value?.event_type_data.therapy_evaluation_type_id
    : undefined,
  therapy_caseload_management_types:
    eventToEdit?.value?.event_type_data && eventToEdit?.value?.event_type_data.therapy_caseload_management_types
      ? eventToEdit?.value?.event_type_data.therapy_caseload_management_types
      : undefined,
});

const untilLessThanStartDate = (value: Date) => {
  const until = new Date(value);
  const start = new Date((eventRRule.value?.options as RRule['options']).dtstart);
  const untilLaterThanStart = until > start;
  const untilHourGreaterThanStartHour = until.getHours() > start.getHours();
  const untilHourEqualToStartHour = until.getHours() == start.getHours();
  const untilMinuteGreaterThanStartMinute = until.getMinutes() > start.getMinutes();
  return (
    untilLaterThanStart &&
    (untilHourGreaterThanStartHour || (untilHourEqualToStartHour && untilMinuteGreaterThanStartMinute))
  );
};

const isWeeklyAndNoWeekdayChosen = (value: number[]) => {
  if ((eventRRule.value?.options as RRule['options']).freq === RRule.WEEKLY && isCustomRecurrence.value) {
    return !!value.length;
  } else {
    return true;
  }
};

const recurringWeekdayUpdate = (weekdays: number[]) =>
  ((eventRRule.value.options as RRule['options']).byweekday = weekdays as number[]);

const rules = computed(() => ({
  rrule: {
    options: {
      until: {
        requiredIfCustom: helpers.withMessage('Please specify a date after the start date', untilLessThanStartDate),
      },
      byweekday: {
        requiredIfCustom: helpers.withMessage('Please select a weekday', isWeeklyAndNoWeekdayChosen),
      },
    },
  },
  attendees: { required: helpers.withMessage('Please select an attendee(s)', required) },
  selected_evaluation_attendee: {
    requiredIfCustom: helpers.withMessage(
      'Please select an Attendee',
      requiredIf(() => selectedEventTypeIs([EVENT_TYPE.EVALUATION])),
    ),
  },
  duration: {
    lessThanEightHours: helpers.withMessage(
      'Event exceeds the maximum allowed duration of 8 hours. Please adjust start or end Time',
      maxValue(480),
    ),
  },
  discipline_id: {
    requiredIfCustom: helpers.withMessage(
      'Please select a Discipline',
      requiredIf(() => selectedEventTypeIs(disciplineAttendeeRequiredEventTypes as EVENT_TYPE[])),
    ),
  },
  description: {
    requiredIfCustom: helpers.withMessage(
      'Notes are required for this event type',
      requiredIf(() => selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])),
    ),
  },
  therapy_event_type: { required },
  assessment_record_form_id: {
    requiredIfCustom: helpers.withMessage(
      'Please select an Assessment',
      requiredIf(selectedEventTypeIs([EVENT_TYPE.EVALUATION])),
    ),
  },
  due_at: {
    requiredIfCustom: helpers.withMessage(
      'Please select a Due Date',
      requiredIf(selectedEventTypeIs([EVENT_TYPE.EVALUATION])),
    ),
  },
  therapy_evaluation_type_id: {
    requiredIfCustom: helpers.withMessage(
      'Please select an Evaluation Type',
      requiredIf(selectedEventTypeIs([EVENT_TYPE.EVALUATION])),
    ),
  },
  session_types: {
    requiredIfCustom: helpers.withMessage(
      'Please select a Session Type',
      requiredIf(selectedEventTypeIs([EVENT_TYPE.SESSION])),
    ),
  },
  therapy_caseload_management_types: {
    requiredIfCustom: helpers.withMessage(
      'Please select a Caseload Management type',
      requiredIf(selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])),
    ),
  },
}));

const v$ = useVuelidate(rules, newEvent, { $scope: v$scope });

const serviceDateErrors = computed(() => v$.value.$getResultsForChild(serviceDateErrorKey));

const init = async () => {
  if (unref(eventToEditSelected)) {
    const event = unref(eventToEdit);

    if (availableAttendees.value.length) {
      setUpdatedEventData(event as DetailedEventInterface);
      eventDataSet.value = true;
    }
  } else selectedAttendees.value = await eventsModalsStore.getPreSelectedAttendees();
};

const setUpdatedEventData = async (event: DetailedEventInterface) => {
  newEvent.therapy_evaluation_type_id = event.event_type_data?.therapy_evaluation_type_id;
  newEvent.therapy_event_type = event.therapy_event_type;

  const staticAvailableAttendees = availableAttendees.value.map((attendee) => ({ ...attendee }));

  selectedAttendees.value = [];
  event.attendees.forEach((attendee) => {
    const foundAttendee = availableAttendees.value.find((a) => a.value === attendee.attendee_id);
    if (foundAttendee) {
      const staticAttendee = staticAvailableAttendees.find((a) => a.value === foundAttendee.value);
      if (staticAttendee) {
        selectedAttendees.value.push({ ...staticAttendee, attendee_id: attendee.id });
      }
    }
  });

  evaluationEditAttendees.value = staticAvailableAttendees;

  if (selectedEventTypeIs([EVENT_TYPE.EVALUATION]) && selectedAttendees.value.length) {
    const selectedValue = selectedAttendees.value[0].value;
    newEvent.selected_evaluation_attendee = evaluationEditAttendees.value.find((a) => a.value === selectedValue);
    await getTherapyEventAllowedDateRange(selectedAttendees.value[0].organizations[0].id);
  }

  newEvent.attendees = selectedAttendees.value;
  const startAt = new Date(event.start_at);

  (eventRRule.value.options as Options).dtstart = new Date(
    Date.UTC(
      startAt.getUTCFullYear(),
      startAt.getUTCMonth(),
      startAt.getUTCDate(),
      eventRRule?.value?.options?.dtstart.getUTCHours(),
      eventRRule?.value?.options?.dtstart.getUTCMinutes(),
      0,
      0,
    ),
  );

  const dueAt = event.event_type_data?.due_at
    ? new Date(event.event_type_data?.due_at?.toString() as string)
    : undefined;
  const cmTypes = event.event_type_data?.therapy_caseload_management_types || [];
  selectedDisciplineId.value = event.discipline?.id as number;
  newEvent.discipline_id = event.discipline?.id as number;
  newEvent.description = getEventDescription(event);

  newEvent.assessment_record_form_id = (event.event_type_data?.assessment_record_form_id as number) || undefined;

  if (newEvent.assessment_record_form_id) {
    eventEvaluationAssessment.value = {
      name: `${event.event_type_data.assessment_record_form?.assessment?.title} - ${event.event_type_data.assessment_record_form.title}`,
      value: event.event_type_data.assessment_record_form.id,
    };
  }

  newEvent.due_at = dueAt || undefined;
  newEvent.therapy_evaluation_type_id = event.event_type_data?.therapy_evaluation_type_id || undefined;
  newEvent.documents = event.documents as File[];
  newEvent.session_types = Array.isArray(event.event_type_data?.session_types)
    ? event.event_type_data?.session_types
    : [getTherapySessionTypeObject(0, eventSessionTypes.value[0]?.value)];
  newEvent.therapy_caseload_management_types =
    cmTypes?.length > 1 ? (cmTypes?.map((item) => item.id) as number[]) : cmTypes[0]?.id;
};

watch(newEvent, (value) => {
  if (eventToEditSelected.value && eventToEdit?.value?.event_type_data && !!value.assessment_record_form_id) {
    showEvaluationAssessmentsFiles.value =
      value.assessment_record_form_id === eventToEdit?.value?.event_type_data.assessment_record_form_id;
  }
});

/**
 * watches for changes to discipline_id to shows only assessment of selected discipline
 */
watch(
  () => newEvent.discipline_id,
  (value) => {
    if (newEvent.discipline_id !== undefined) {
      const sourceAssessments = evaluationAssessmentsTypesRequest.value.filter(
        (item) => item.discipline_id === null || value === item.discipline_id,
      );
      setAssessments(sourceAssessments);
      if (!eventToEditSelected.value) {
        newEvent.assessment_record_form_id = undefined;
      }
    } else {
      setAssessments();
    }
  },
  { deep: true },
);

watch([endCalendarModel, startCalendarModel], async () => {
  if (!isElumaClinician.value || !hasCaseloadOrganizationId.value) return;
  isSplitCaseload.value = false;
  if (caseloadOrganization?.value && caseloadOrganization?.value.id) {
    await getTherapyEventAllowedDateRange(caseloadOrganization?.value.id);
  }

  checkForHourlyContractType();
  adjustDateLimit();
  checkContractDateValid(startCalendarModel.value);

  if (selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])) {
    checkCaseloadManagementDateValid(startCalendarModel.value);
  }
});

watch(
  caseloadOrganization as Ref<ClinicianOrganizationInterface>,
  async (newValue: ClinicianOrganizationInterface, oldValue: ClinicianOrganizationInterface) => {
    if (!isElumaClinician.value || !hasCaseloadOrganizationId.value) return;
    isSplitCaseload.value = false;

    await getTherapyEventAllowedDateRange(newValue.id);

    if (oldValue) checkForSplitCaseload(!!unref(oldValue).id);
    checkForHourlyContractType();
    adjustDateLimit();
  },
);

/**
 * watches if it is a custom recurrence or if the frequency is monthly
 * if this is the case, the byweekday property will be cleared out
 */
watchEffect(() => {
  if (eventRRule.value) {
    if (!isCustomRecurrence.value || eventRRule?.value?.options?.freq === RRule.MONTHLY) {
      (eventRRule.value.options as RRule['options']).byweekday = (
        eventRRule.value.origOptions as RRule['options']
      ).byweekday;
      (eventRRule.value.options as RRule['options']).bynweekday = null;
    }
  }
});

watchEffect(() => {
  if (
    unref(selectedAttendees).length === 1 &&
    selectedEventTypeIs([EVENT_TYPE.SESSION]) &&
    (newEvent.session_types as AttendeeSessionTypesData[]).length === 1
  ) {
    (newEvent.session_types as AttendeeSessionTypesData[])[0].person_id = unref(selectedAttendees)[0].value;
  }
});

// This computed ref is necessary to send updates to the SelectAttendees component
const isGroupEventType: ComputedRef<boolean> = computed(() => {
  return !selectedEventTypeIs([EVENT_TYPE.RECORD_REVIEW, EVENT_TYPE.CONSULTATION, EVENT_TYPE.EVALUATION]);
});

const updateAttendees = (attendees: AttendeeDropdownOptionInterface[]) => {
  v$.value.attendees.$touch();

  selectedAttendees.value = attendees;

  updateSessionTypesArray();
};

const getAttendeeWindow = async (attendee: Ref<AttendeeDropdownOptionInterface>) => {
  const evaluationAttendee = attendee.value;
  updateAttendees([evaluationAttendee]);
  await getTherapyEventAllowedDateRange(evaluationAttendee.organizations[0].id);
};

/**
 * if its caseload or evaluation we need the date ranges in the contract and evaluations allowed in that time
 * @param attendee
 */
const getTherapyEventAllowedDateRange = async (id: number) => {
  await therapyStore.getTherapyEventAllowedDateRange(id, dateUtil.formatDateForApi(startCalendarModel.value));
};

/**
 * if the user is a clinician in an org with an Eluma contract take the selected
 * students org id and get the allowed date range for IEP
 */
const adjustDateLimit = async () => {
  setAllowedTherapyEventElumaContractRange();
};

/**
 * checks the currently selected organization in cm events and determines whether they need
 * a single dropdown or a multiselect dropdown. split_caseload == true means singular dropdown
 */
const checkForSplitCaseload = async (clearAttendees = true) => {
  if (clearAttendees) newEvent.attendees = [];
  isSplitCaseload.value = !!therapyStore.allowedDateRange?.contract_windows[0]?.split_caseload;
};

const checkForHourlyContractType = () => {
  if (therapyStore.allowedDateRange?.contract_windows[0]?.contract_type === CONTRACT_BILLING_TYPE.HOURLY) {
    caseloadManagementTypes.value = therapyStore.allowedDateRange?.contract_windows[0]?.caseload_managements?.map(
      ({ id, name }) => {
        return {
          name: name as string,
          value: id,
        };
      },
    ) as { name: string; value: number }[];
  }
};

const updateDiscipline = () => {
  v$.value.discipline_id.$touch();
  selectedDisciplineId.value = newEvent.discipline_id;
};

const updateAttendeeSessionType = (attendeeSessionTypeObject: AttendeeSessionTypesData) => {
  const arrayIndex = newEvent.session_types?.findIndex((sessionTypeObject) => {
    return attendeeSessionTypeObject.person_id === sessionTypeObject.person_id;
  });
  if (Number(arrayIndex) > -1) {
    (newEvent.session_types as AttendeeSessionTypesData[])[Number(arrayIndex)].therapy_session_type.id =
      attendeeSessionTypeObject.therapy_session_type.id;
  }
};

const emitSaveNewEvent = async () => {
  const payload: EventCreationFormInterface = { ...newEvent };
  saving.value = true;
  if (unref(eventToEditSelected)) {
    await therapyStore
      .updateEvent(payload, unref(eventToEdit)?.id as number, unref(editEventConditions), unref(isRecurringEventEdit))
      .then(() => {
        emit(EMIT.SAVE_NEW_EVENT);
      })
      .catch((errors) => emit(EMIT.SAVE_EVENT_FAILED, errors));
  } else {
    await therapyStore
      .createEvent(payload, rRuleRef?.value?.id as number)
      .then(() => emit(EMIT.SAVE_NEW_EVENT))
      .catch((errors) => emit(EMIT.SAVE_EVENT_FAILED, errors));
  }
};

const emitCloseModal = () => {
  emit(EMIT.CLOSE_MODAL);

  if (isAttendeesSelectedFromDashboard.value) isAttendeesSelectedFromDashboard.value = false;
};

const emitDocumentEvent = () => emit(EMIT.DOCUMENT_EVENT);

const onCancelConfirm = () => (confirmEventOpen.value = false);

/**
 * reset the warning object
 */
const resetDateWarningMessage = () => {
  warningMessage.value = undefined;
  warningDetails.value = [];
};

const resetCMDateWarningMessage = () => {
  cmWarningMessage.value = undefined;
};

/**
 * check the selected Date to see if its within the params of the contract start/end
 * @param date
 */
const checkContractDateValid = (date: Date) => {
  if (therapyStore.allowedDateRange?.contract_windows[0]) {
    resetDateWarningMessage();
    showWarning.value = false;
    warningMessage.value = 'Event dates fall outside the contract dates';

    for (const range of therapyStore.allowedDateRange.contract_windows) {
      if (dateUtil.dateIsNotValidInWindow(range, date)) {
        warningDetails.value.push(`${range.start_at} to ${range.end_at}`);
        showWarning.value = true;
      } else {
        resetDateWarningMessage();
        showWarning.value = false;
        warningMessage.value = 'Event dates fall outside the contract dates';
        break;
      }
    }
  }
};

/**
 * check the selected Date to see if its within the params of the caseloadManagement start/end
 * 30 days in the past window
 * @param date
 */
const checkCaseloadManagementDateValid = (date: Date) => {
  if (therapyStore.allowedDateRange?.caseload_management_windows[0]) {
    const allowedDates = therapyStore.allowedDateRange?.caseload_management_windows[0];
    resetCMDateWarningMessage();
    showCMWarning.value = false;
    cmWarningMessage.value =
      'Caseload Management Events can only be created up to 30 days in the past, but can not be created in the future.';
    if (dateUtil.dateIsNotValidInWindow(allowedDates, date)) showCMWarning.value = true;
  }
};

// Since we have multiple calendar components, we cannot use the same
// v-model value for each since changing one will change the others.
// These are event handlers to manually change the eventRRule start
// or end dates without resetting the time values
const newStartDateSelected = (date: Date) => {
  checkContractDateValid(date);

  //if caseload mgmt event then check the date windows
  if (selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])) {
    checkCaseloadManagementDateValid(date);
  }

  const startHours = eventRRule.value.options?.dtstart.getHours() as number;
  const startMinutes = eventRRule.value.options?.dtstart.getMinutes() as number;
  date.setHours(startHours, startMinutes);
  if (eventRRule.value.options) {
    eventRRule.value.options.dtstart = date;
    // if the until value is less than the start date, or if this isn't a custom recurrence event
    // adjust the end date as well
    if (!isRecurring.value) newEndDateSelected(new Date(date));
    else if (
      ((eventRRule.value.options?.until as Date) < date ||
        !isCustomRecurrence.value ||
        !eventRRule.value.options?.count) &&
      !eventToEditSelected.value
    ) {
      setCalendarLastDayOfSchool(startHours, startMinutes);
    }
  }
};

const newEndDateSelected = (date: Date) => {
  checkContractDateValid(date);
  const hours = eventRRule.value.options?.until?.getHours();
  const minutes = eventRRule.value.options?.until?.getMinutes();
  date.setHours(hours as number, minutes);

  if (eventRRule.value.options) {
    endCalendarModel.value = date;
    eventRRule.value.options.until = date;
  }
};

const getServiceEventToBeSelected = async () => {
  const event = selectedAttendees.value.flatMap((attendee) => {
    return attendee.disciplines
      .filter((discipline) => discipline.is_active)
      .map((discipline) => discipline.service?.name);
  });

  if (!event.length) return (preselectedEventType.value = EVENT_TYPE.CASELOAD_MANAGEMENT as EVENT_TYPE);

  preselectedEventType.value = [
    EVENT_TYPE.SESSION,
    EVENT_TYPE.EVALUATION,
    EVENT_TYPE.RESPONSE_TO_INTERVENTION,
    EVENT_TYPE.CONSULTATION,
    EVENT_TYPE.IEP_MEETING,
    EVENT_TYPE.RECORD_REVIEW,
    EVENT_TYPE.CASELOAD_MANAGEMENT,
  ].find((eventType) => event.includes(eventType));
};
let attendeesSelectedFromDashboard = [...eventsModalsStore.preSelectedAttendees];

const checkIfEventMatchesWithStudent = async (label: EVENT_TYPE) => {
  const students: string[] = [];
  selectedAttendees.value = [...attendeesSelectedFromDashboard];
  attendeesSelectedFromDashboard.forEach((attendee) => {
    if (attendee.disciplines.length === 1) {
      attendee.disciplines.filter((discipline) => {
        if (label === EVENT_TYPE.CASELOAD_MANAGEMENT) {
          return (selectedAttendees.value = [...attendeesSelectedFromDashboard]);
        }

        const hasEqual = (discipline.service?.name === label && discipline.is_active) as boolean;
        if (!hasEqual) {
          students.push(attendee.name);
          selectedAttendees.value.splice(selectedAttendees.value.indexOf(attendee), 1);
        }
      });
    }

    if (attendee.disciplines.length > 1) {
      const hasTwoOrMoreDisciplines = attendee.disciplines.map(
        (discipline) => discipline.service?.name === label && discipline.is_active,
      );

      if (label === EVENT_TYPE.CASELOAD_MANAGEMENT) {
        return (selectedAttendees.value = [...attendeesSelectedFromDashboard]);
      }

      if (!hasTwoOrMoreDisciplines.includes(true)) {
        students.push(attendee.name);
        selectedAttendees.value.splice(selectedAttendees.value.indexOf(attendee), 1);
      }
    }
  });

  if (students.length) {
    students.length > 1
      ? (filteredMessage.value = `${
          students.slice(0, -1).join(', ') + ' and ' + students.at(-1)
        } do not have  ${label} in their treatment plan.`)
      : (filteredMessage.value = `${students} does not have ${label} in their treatment plan.`);
  }
};

const selectEventType = async (label: EVENT_TYPE) => {
  filteredMessage.value = '';
  showCMWarning.value = false;

  if (isAttendeesSelectedFromDashboard.value) {
    await checkIfEventMatchesWithStudent(label);
  } else {
    selectedAttendees.value = [];
  }
  selectedDisciplineId.value = undefined;
  newEvent.discipline_id = undefined;
  newEvent.description = '';
  newEvent.documents = [];
  newEvent.therapy_caseload_management_types = [];
  newEvent.session_types = eventSessionTypes.value.length
    ? [getTherapySessionTypeObject(0, eventSessionTypes.value[0].value)]
    : [];
  newEvent.therapy_event_type = label;

  resetOneYearAllowedDateRange();
  rRuleRef.value = recurrenceOptions.value[0];
  recurrenceUpdate(ref(recurrenceOptions.value[0]));
  v$.value.$reset();
  if (label === EVENT_TYPE.CASELOAD_MANAGEMENT) {
    checkCaseloadManagementDateValid(startCalendarModel.value);
  }
};

const uploadFiles = (event: FileUploadUploaderEvent) => (newEvent.documents = event.files as File[]);

const removeFile = (index: number) => newEvent.documents?.splice(index, 1);

const getAttendeeSessionType = (attendee: AttendeeDropdownOptionInterface) => {
  const arrayIndex = newEvent.session_types?.findIndex((sessionTypeObject) => {
    return attendee.value === sessionTypeObject.person_id;
  });
  if (Number(arrayIndex) > -1) {
    const attendeeSessionType = (newEvent.session_types as AttendeeSessionTypesData[])[Number(arrayIndex)];
    attendeeSessionType.person_id = attendee.value;
    return attendeeSessionType;
  }
  return getTherapySessionTypeObject(attendee.value, eventSessionTypes.value[0].value);
};

const checkDisabled = (sessionTypeId: number) => {
  if (!unref(newEvent.attendees).length) {
    return false;
  }
  const attendeeSessionType = unref(newEvent.attendees)[0].disciplines[0].treatment_service_type?.id;
  const regularSessionTypeId = eventSessionTypes.value.find((sessionType) => sessionType.name === 'Regular')?.value;
  const makeupSessionTypeId = eventSessionTypes.value.find((sessionType) => sessionType.name === 'Make up')?.value;

  return !(
    attendeeSessionType === sessionTypeId ||
    sessionTypeId === regularSessionTypeId ||
    sessionTypeId === makeupSessionTypeId
  );
};

const updateSessionTypesArray = () => {
  if (
    unref(selectedAttendees).length !== (newEvent.session_types as AttendeeSessionTypesData[]).length &&
    selectedEventTypeIs([EVENT_TYPE.SESSION])
  ) {
    const sessionTypeMatchArray: AttendeeSessionTypesData[] = [];
    unref(selectedAttendees).forEach((attendee) => {
      const attendeeSessionType = newEvent.session_types?.find((sessionTypeObject) => {
        return attendee.value === sessionTypeObject.person_id;
      });

      if (attendeeSessionType) sessionTypeMatchArray.push(attendeeSessionType);
      else {
        sessionTypeMatchArray.push(getTherapySessionTypeObject(attendee.value, eventSessionTypes.value[0].value));
      }
    });
    if (!sessionTypeMatchArray.length) {
      sessionTypeMatchArray.push(getTherapySessionTypeObject(0, eventSessionTypes.value[0].value));
    }
    newEvent.session_types = sessionTypeMatchArray;
  }
};

/**
 * when selecting reoccuring events in a school year limit to the upcoming school year of the
 * start date selected.
 */
const setCalendarLastDayOfSchool = (hours: number, minutes: number) => {
  if (eventRRule.value.options) {
    eventRRule.value.options.until = dateUtil.getLastSchoolDay(eventRRule.value.options?.dtstart);
    eventRRule.value.options.until.setHours(hours as number, (minutes as number) + duration.value);
    endCalendarModel.value = eventRRule.value.options.until;
  }
};

const recurrenceUpdate = (event: DropdownChangeEvent | Ref<RecurrenceDropdownOption>) => {
  const hours = eventRRule.value.options?.dtstart?.getHours() as number;
  const minutes = eventRRule.value.options?.dtstart?.getMinutes() as number;
  eventRRule.value = event.value.value;

  // If the non recurring option has been chosen, set the until date to dtstart date and add the duration
  if (!event.value.id && eventRRule.value.options?.until) {
    const eventRRuleOptions = JSON.parse(JSON.stringify(eventRRule.value.options));
    eventRRule.value.options.until = new Date(eventRRuleOptions.dtstart);

    eventRRule.value.options.until.setHours(hours as number, (minutes as number) + duration.value);
    endCalendarModel.value = eventRRule.value.options.until;
  } else if (event.value.id && eventRRule.value.options) {
    const sameDay = eventRRule.value.options.until?.getDay() == eventRRule.value.options.dtstart?.getDay();
    const sameMonth = eventRRule.value.options.until?.getMonth() == eventRRule.value.options.dtstart?.getMonth();

    // If a recurring option has been chosen, check to see if the until date should be set to the default
    // last day of school
    if (sameDay && sameMonth && event.value.value.options) setCalendarLastDayOfSchool(hours, minutes);
  }
};

const isRecurringEventEdit = computed<boolean>(() => {
  return (
    eventToEditSelected.value && !selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT]) && eventToEdit?.value?.rrule
  );
});
const hasCaseloadError = computed(() => {
  const event = eventToEditSelected.value ? eventToEdit?.value : newEvent;
  return (
    selectedDisciplineId.value && !disciplineOnlyRequiredEventTypes.includes(event?.therapy_event_type as EVENT_TYPE)
  );
});

const sortedCaseloadManagementTypes = computed(() => {
  return [...caseloadManagementTypes.value].sort((a, b) => a.name.localeCompare(b.name));
});

const setAssessments = (sourceAssessments: AssessmentData[] | undefined = undefined) => {
  const filterAssessments = evaluationAssessmentsTypesRequest.value.filter(
    (item) =>
      item.discipline_id == null || availableDisciplines.value.some(({ value }) => value === item.discipline_id),
  );

  sourceAssessments = sourceAssessments === undefined ? filterAssessments : sourceAssessments;

  evaluationAssessments.value = sourceAssessments
    .map(({ title: assessmentTitle, assessment_record_forms }) => {
      return assessment_record_forms.map(({ id: formId, title: formTitle }) => ({
        name: `${assessmentTitle} ${formTitle == null ? '' : ' - ' + formTitle}`,
        value: formId,
      }));
    })
    .flat();

  if (eventEvaluationAssessment.value) {
    const hasAssessment = evaluationAssessments.value.findIndex(
      (item) => item.value === eventEvaluationAssessment.value.value,
    );

    if (hasAssessment < 0) {
      evaluationAssessments.value.push(eventEvaluationAssessment.value);
    }
  }

  evaluationAssessments.value.sort((a, b) => a.name.localeCompare(b.name));
};
const onClear = () => {
  preselectedEventType.value = undefined;
  isAttendeesSelectedFromDashboard.value = false;
  filteredMessage.value = '';
  preSelectedAttendees.value = [];
  newEvent.attendees = [];
  attendeesSelectedFromDashboard = [];
  onDismissMessage();
  v$.value.$reset();
};

const onDismissMessage = () => {
  filteredMessage.value = '';
};

const onDownloadDocument = async (id: number, name: string) => {
  await documentsStore.downloadDocument(id, name);
};

const hasCaseloadOrganizationId = computed(() => caseloadOrganization.value && caseloadOrganization.value.id);

const getEventDescription = (event: DetailedEventInterface): string => {
  return event.event_type_data?.description || '';
};

const handleOverlappingError = () => {
  if (!usePersonTypes.isSupportAdmin() && !usePersonTypes.isSuperAdmin()) overlappingError.value = true;
};

const handleOverlappingOk = () => {
  overlappingError.value = false;
};

onMounted(() => init());

onUnmounted(() => {
  onClear();
});
</script>

<template>
  <Skeleton v-if="loading" class="m-auto" width="95%" height="54vh" shape="rectangle" />
  <div v-else>
    <div v-if="!calendarStore.isCaseloadManagement(eventToEdit?.therapy_event_type)" class="surface-100 pl-4 pb-2">
      <AppSelectButton
        :items="eventTypes"
        :selected-item="selectedEventType?.id || 0"
        @select-item="selectEventType($event.label as EVENT_TYPE)"
      />
    </div>
    <div v-if="filteredMessage" class="mt-2 ml-5 p-message-icon-position-customized">
      <div class="flex align-content-center justify-content-center w-11 ml-4">
        <Message severity="warn" @close.once="onDismissMessage">
          <div class="p-2 text-base">
            <span class="font-semibold">{{ filteredMessage }}</span>
            <div class="mt-2 text-sm">
              <span>Please select a different event type or select a new student.</span>
            </div>
          </div>
        </Message>
      </div>
    </div>
    <div v-if="eventRRule" class="new-event-modal-content">
      <div class="new-event-date-time">
        <div class="new-event-date-time-section align-items-center w-full inline-flex mb-2">
          <span class="material-icons date-picker-icon mr-3">event</span>
          <Calendar
            v-model="startCalendarModel"
            :min-date="allowedDateRange.start"
            :max-date="allowedDateRange.end"
            class="new-event-date"
            date-format="DD, MM dd"
            :class="{
              'p-invalid': serviceDateErrors?.rrule.options.dtstart.$invalid,
            }"
            @date-select="newStartDateSelected"
            @blur="v$.rrule.$touch"
          />

          <Calendar
            ref="startTimeElement"
            v-model="(eventRRule.options as RRule['options']).dtstart"
            class="new-event-time"
            input-class="uppercase"
            :class="{
              'p-invalid': v$.duration.$invalid && v$.duration.$dirty,
            }"
            :time-only="true"
            hour-format="12"
            :step-minute="5"
            autocomplete="off"
            :min-date="allowedDateRange.start"
            :max-date="allowedDateRange.end"
            @update:model-value="newStartDateSelected"
            @blur="v$.rrule.$touch"
          />
          <span class="new-event-separator">to</span>

          <Calendar
            ref="endTimeElement"
            v-model="(eventRRule.options as RRule['options']).until"
            class="new-event-time text-sm"
            input-class="uppercase"
            :class="{
              'p-invalid':
                (v$.rrule.options.until.$invalid && v$.rrule.options.until.$dirty) ||
                (v$.duration.$invalid && v$.duration.$dirty),
            }"
            :time-only="true"
            hour-format="12"
            :step-minute="5"
            autocomplete="off"
            @update:model-value="newEndDateSelected"
            @blur="v$.rrule.$touch"
          />
          <span v-if="hostTimezoneAbbr" class="ml-2 font-bold" v-text="hostTimezoneAbbr" />
        </div>
        <div v-for="error of v$.rrule.$errors" :key="error.$uid" class="input-errors w-full ml-6">
          <div class="p-error">{{ error.$message }}</div>
        </div>
        <div v-for="error of v$.duration.$errors" :key="error.$uid" class="input-errors w-full ml-6">
          <div class="p-error">{{ error.$message }}</div>
        </div>
        <div
          v-if="!selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT, EVENT_TYPE.RECORD_REVIEW])"
          class="new-event-date-time-section"
        >
          <Dropdown
            v-model="rRuleRef"
            option-label="label"
            data-key="id"
            :options="recurrenceOptions"
            class="event-recurring-dropdown"
            @change="recurrenceUpdate"
          />
        </div>
        <div
          v-if="!isCustomRecurrence && rRuleRef && !rRuleRef?.value.options?.count"
          class="new-event-date-time-section"
        >
          <span class="new-event-separator">until</span>
          <Calendar
            v-model="endCalendarModel"
            class="new-event-date"
            date-format="DD, MM dd"
            :class="{
              'p-invalid':
                (v$.rrule.options.until.$invalid && v$.rrule.options.until.$dirty) ||
                serviceDateErrors?.rrule.options.until.$invalid,
            }"
            @date-select="newEndDateSelected"
          />
        </div>
      </div>
      <div v-if="isCustomRecurrence" class="custom-recurrence__container inline-flex">
        <div class="field col required p-0 mr-2">
          <label for="interval">Repeat every</label>
          <div class="inline-flex">
            <InputNumber
              id="interval"
              v-model="(eventRRule.options as RRule['options']).interval"
              class="w-3rem mr-2"
              :min="1"
              :max="12"
              required
            />

            <Dropdown
              id="frequency"
              v-model="(eventRRule.options as RRule['options']).freq"
              class="event-recurring-dropdown border-none"
              required
              option-label="label"
              option-value="value"
              :options="frequencyIntervals"
              placeholder="Select Interval"
            />
          </div>
        </div>
        <WeekdayPicker
          v-if="(eventRRule.options as RRule['options']).freq === RRule.WEEKLY"
          :class="{
            'p-invalid': v$.rrule.options.byweekday.$invalid && v$.rrule.options.byweekday.$dirty,
          }"
          :selected-values="(eventRRule.options as RRule['options']).byweekday || []"
          @update="recurringWeekdayUpdate"
        />
        <div class="field col required p-0 ml-2">
          <label for="ends_on">Ends on</label>
          <Calendar
            id="ends_on"
            v-model="endCalendarModel"
            class="new-event-date"
            date-format="mm/dd/yy"
            :class="{
              'p-invalid':
                (v$.rrule.options.until.$invalid && v$.rrule.options.until.$dirty) ||
                serviceDateErrors?.rrule.options.until.$invalid,
            }"
            @date-select="newEndDateSelected"
          />
        </div>
        <Calendar
          v-if="!isCustomRecurrence && rRuleRef && !rRuleRef?.value.options?.count"
          v-model="endCalendarModel"
          class="new-event-date"
          date-format="D, M dd"
          :class="{
            'p-invalid':
              (v$.rrule.options.until.$invalid && v$.rrule.options.until.$dirty) ||
              serviceDateErrors?.rrule.options.until.$invalid,
          }"
          @date-select="newEndDateSelected"
        />
      </div>
      <ServiceDateError
        v-if="hasCaseloadError && selectedEventType"
        :rrule="eventRRule"
        :attendees="selectedAttendees"
        :event-type="selectedEventType?.label"
        :discipline-id="selectedDisciplineId"
        :vuelidate-key="serviceDateErrorKey"
        :scope="v$scope"
      />

      <CalendarWarning v-if="showWarning" :message="warningMessage as string" :details="warningDetails" />
      <CalendarWarning v-if="showCMWarning" :message="cmWarningMessage as string" />
      <CalendarOverlappingEvents
        v-if="duration && $tenant.tenantSettingEnabled(TENANT_SETTINGS.OVERLAPPING_EVENTS)"
        :duration="duration"
        :event-id="eventToEdit?.id"
        :is-recurring="isRecurring"
        :rrule="eventRRule"
        @error="handleOverlappingError"
        @ok="handleOverlappingOk"
      />
      <template v-if="selectedEventTypeIs([EVENT_TYPE.EVALUATION])">
        <div class="flex gap-1 w-full justify-content-start align-content-center mt-3">
          <span class="material-icons group-icon material-symbols-outlined mr-2 align-content-center">person</span>
          <Dropdown
            v-if="eventToEditSelected"
            v-model="newEvent.selected_evaluation_attendee"
            :options="evaluationEditAttendees"
            filter
            class="w-full"
            option-label="name"
            placeholder="Select an Attendee"
            :class="{ 'p-invalid': v$.selected_evaluation_attendee.$invalid && v$.selected_evaluation_attendee.$dirty }"
            :blur="v$.selected_evaluation_attendee.$touch"
            @change="getAttendeeWindow"
          />
          <Dropdown
            v-else
            v-model="newEvent.selected_evaluation_attendee"
            :options="availableAttendees"
            filter
            class="w-full"
            option-label="name"
            placeholder="Select an Attendee"
            :class="{ 'p-invalid': v$.selected_evaluation_attendee.$invalid && v$.selected_evaluation_attendee.$dirty }"
            :blur="v$.selected_evaluation_attendee.$touch"
            @change="getAttendeeWindow"
          />
        </div>
        <div v-for="error of v$.selected_evaluation_attendee.$errors" :key="error.$uid" class="input-errors ml-6 mt-1">
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </template>
      <template v-if="selectedEventTypeIs(disciplineAttendeeRequiredEventTypes as EVENT_TYPE[])">
        <template v-if="selectedEventTypeIs([EVENT_TYPE.SESSION, EVENT_TYPE.RESPONSE_TO_INTERVENTION])">
          <SelectAttendees
            :available-attendees="availableAttendees"
            :selected-attendees="unref(newEvent.attendees)"
            :group-therapy="isGroupEventType"
            wrapper-class="mt-4"
            :class="{ 'p-invalid': v$.attendees.$invalid && v$.attendees.$dirty }"
            :blur="v$.attendees.$touch"
            @update="updateAttendees"
          />
          <div v-for="error of v$.attendees.$errors" :key="error.$uid" class="input-errors ml-6 mt-1">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </template>
        <div class="discipline-selector flex align-items-center mt-4">
          <span class="material-icons discipline-icon material-symbols-outlined mr-3">work</span>
          <Dropdown
            id="disciplines__dropdown"
            v-model="newEvent.discipline_id"
            class="w-full"
            :options="availableDisciplines"
            option-value="value"
            option-label="name"
            placeholder="Select a Discipline"
            :class="{ 'p-invalid': v$.discipline_id.$invalid && v$.discipline_id.$dirty }"
            @blur="v$.discipline_id.$touch"
            @change="updateDiscipline"
          />
        </div>
        <div v-for="error of v$.discipline_id.$errors" :key="error.$uid" class="input-errors ml-6 mt-1">
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </template>
      <div
        v-if="!selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])"
        class="description-input flex align-items-center mt-4"
      >
        <span class="material-icons description-icon material-symbols-outlined mr-3 text-color-orange">notes</span>
        <Textarea
          v-model="newEvent.description"
          class="w-full"
          rows="1"
          :auto-resize="true"
          placeholder="Add Description"
        />
      </div>
      <Divider class="border-200" />
    </div>
    <div class="new-event-event-type py-0">
      <!--Session-->
      <template v-if="selectedEventTypeIs([EVENT_TYPE.SESSION])">
        <div class="field">
          <div class="pt-0 inline-flex align-content-center pb-3">
            <label class="font-semibold text-sm pr-1">Session Type</label>
            <Icon v-tooltip.top="sessionTypesTooltip" name="info" class="session-type-info" />
          </div>
          <template v-if="unref(newEvent.attendees)?.length <= 1 && newEvent?.session_types?.length">
            <Dropdown
              v-model="(newEvent.session_types as AttendeeSessionTypesData[])[0].therapy_session_type.id"
              option-value="value"
              option-label="name"
              :option-disabled="(option) => checkDisabled(option.value)"
              :class="{
                'p-invalid': v$.session_types.$invalid && v$.session_types.$dirty,
              }"
              :options="eventSessionTypes"
              @blur="v$.session_types.$touch"
            />
          </template>
          <template v-else>
            <div class="flex flex-column gap-3">
              <div v-for="attendee in unref(newEvent.attendees)" :key="attendee.value">
                <GroupSessionTypeSelect
                  :attendee="attendee"
                  :event-session-types="eventSessionTypes"
                  :selected-session-type="getAttendeeSessionType(attendee)"
                  @update="updateAttendeeSessionType"
                />
              </div>
            </div>
          </template>
        </div>
      </template>

      <!--Evaluation-->
      <template v-if="selectedEventTypeIs([EVENT_TYPE.EVALUATION])">
        <div class="field required mt-0">
          <div class="field">
            <label>Evaluation Type</label>
            <Dropdown
              id="evaluation_input"
              v-model="newEvent.therapy_evaluation_type_id"
              option-value="value"
              option-label="name"
              :class="{
                'p-invalid': v$.therapy_evaluation_type_id.$invalid && v$.therapy_evaluation_type_id.$dirty,
              }"
              :disabled="!selectedAttendees.length"
              :options="therapyStore.eventEvaluations"
              @blur="v$.therapy_evaluation_type_id.$touch"
            />
          </div>
          <div class="field required">
            <label>Assessment</label>
            <Dropdown
              id="assessment_input"
              v-model="newEvent.assessment_record_form_id"
              option-value="value"
              option-label="name"
              placeholder="Select an Assessment"
              :filter="true"
              :auto-filter-focus="true"
              :class="{
                'p-invalid': v$.assessment_record_form_id.$invalid && v$.assessment_record_form_id.$dirty,
              }"
              :options="evaluationAssessments"
              @blur="v$.assessment_record_form_id.$touch"
            />
            <div v-for="error of v$.assessment_record_form_id.$errors" :key="error.$uid" class="input-errors mt-1">
              <div class="p-error">{{ error.$message }}</div>
            </div>

            <EventEvaluationAssessmentsFiles v-if="showEvaluationAssessmentsFiles" class="mt-2" :event="eventToEdit" />
          </div>
        </div>
        <div class="field required">
          <label>Due Date</label>
          <Calendar
            id="due_date_input"
            v-model="newEvent.due_at"
            class="new-event-date"
            :class="{ 'p-invalid': v$.due_at.$invalid && v$.due_at.$dirty }"
            date-format="D, M dd"
            @blur="v$.due_at.$touch"
          />
          <div v-for="error of v$.due_at.$errors" :key="error.$uid" class="input-errors mt-1">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>

      <!--Caseload Mangagment-->
      <template v-if="selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])">
        <div class="field required mt-2">
          <label for="caseloadOrganization_single">Organization</label>
          <Dropdown
            id="caseloadOrganization_single"
            v-model="caseloadOrganization"
            :options="filteredServiceProviderOrganizations"
            class="w-full"
            panel-class="new-event-attendee-select"
            data-key="id"
            option-label="name"
            :filter="true"
            placeholder="Select Organization"
          />
        </div>
        <div v-if="!isSplitCaseload" class="field required mt-2">
          <label for="regarding_multi">Regarding</label>
          <MultiSelect
            id="regarding_multi"
            v-model="newEvent.attendees"
            :options="availableAttendees"
            class="w-full"
            panel-class="new-event-attendee-select"
            data-key="value"
            option-label="name"
            :filter="true"
            display="chip"
            placeholder="Add People"
            :disabled="!hasCaseloadOrganizationId"
            :class="{ 'p-invalid': v$.attendees.$invalid && v$.attendees.$dirty }"
            @blur="v$.attendees.$touch"
          />
          <div v-for="error of v$.attendees.$errors" :key="error.$uid" class="input-errors mt-1">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div v-else class="field required mt-2">
          <label for="regarding_single_cm">Regarding</label>
          <Dropdown
            id="regarding_single_cm"
            v-model="newEvent.attendees[0]"
            :options="availableAttendees"
            class="w-full"
            panel-class="new-event-attendee-select"
            data-key="value"
            option-label="name"
            :filter="true"
            placeholder="Select Person"
            :class="{ 'p-invalid': v$.attendees.$invalid && v$.attendees.$dirty }"
            @hide="v$.attendees.$touch"
          />
          <div v-for="error of v$.attendees.$errors" :key="error.$uid" class="input-errors mt-1">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
        <div class="field required">
          <label>Caseload Management Type</label>
          <template
            v-if="
              Array.isArray(newEvent.therapy_caseload_management_types) &&
              newEvent.therapy_caseload_management_types.length > 1
            "
          >
            <MultiSelect
              v-model="newEvent.therapy_caseload_management_types"
              :options="sortedCaseloadManagementTypes"
              option-label="name"
              option-value="value"
              :class="{
                'p-invalid':
                  v$.therapy_caseload_management_types.$invalid && v$.therapy_caseload_management_types.$dirty,
              }"
              placeholder="Select Caseload Management Types"
              display="chip"
              @blur="v$.therapy_caseload_management_types.$touch"
            />
          </template>
          <template v-else>
            <Dropdown
              v-model="newEvent.therapy_caseload_management_types"
              :options="sortedCaseloadManagementTypes"
              option-label="name"
              option-value="value"
              :class="{
                'p-invalid':
                  v$.therapy_caseload_management_types.$invalid && v$.therapy_caseload_management_types.$dirty,
              }"
              placeholder="Select Caseload Management Type"
              @blur="v$.therapy_caseload_management_types.$touch"
            />
            <div
              v-for="error of v$.therapy_caseload_management_types.$errors"
              :key="error.$uid"
              class="input-errors mt-1"
            >
              <div class="p-error">{{ error.$message }}</div>
            </div>
          </template>
        </div>
        <div class="field required">
          <label for="notes_input">Notes</label>
          <Textarea
            id="notes_input"
            v-model="newEvent.description"
            class="w-full"
            rows="3"
            :auto-resize="true"
            placeholder="Add Notes"
            :class="{ 'p-invalid': v$.description.$invalid && v$.description.$dirty }"
            @blur="v$.description.$touch"
          />
          <div v-for="error of v$.description.$errors" :key="error.$uid" class="input-errors mt-1">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>

      <template v-if="selectedEventTypeIs([EVENT_TYPE.RECORD_REVIEW, EVENT_TYPE.IEP_MEETING, EVENT_TYPE.CONSULTATION])">
        <div class="field required mt-2">
          <label for="regarding_single">Regarding</label>
          <Dropdown
            id="regarding_single"
            v-model="newEvent.attendees[0]"
            :options="availableAttendees"
            class="w-full"
            panel-class="new-event-attendee-select"
            data-key="value"
            option-label="name"
            :filter="true"
            placeholder="Select Person"
            :class="{ 'p-invalid': v$.attendees.$invalid && v$.attendees.$dirty }"
            @hide="v$.attendees.$touch"
          />
          <div v-for="error of v$.attendees.$errors" :key="error.$uid" class="input-errors mt-1">
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedEventTypeIs(uploadFileEventTypes)" class="field">
        <FileUpload
          mode="basic"
          accept=".pdf, .doc, .docx"
          class="bg-white border-none text-sm font-semibold cursor-pointer"
          name="demo[]"
          choose-label="Upload File"
          upload-icon="pi pi-plus"
          :multiple="true"
          :custom-upload="true"
          :auto="true"
          :max-file-size="100000000"
          invalid-file-size-message="Error: This file exceeds the max file size allowed."
          @uploader="uploadFiles"
        />
        <div class="mt-1 ml-3 m-3 text-xs text-600">
          <p>Allowed types: .pdf, .doc, .docx</p>
          <p>Max File size: 100MB</p>
        </div>
        <div v-if="newEvent.documents?.length" class="surface-100 p-3 mb-0 border-round-lg">
          <div
            v-for="(upload, index) in newEvent.documents"
            :key="upload.name + index"
            class="inline-flex justify-content-between w-full text-sm text-color-gray-dark"
          >
            <div class="col flex align-items-left">
              <Icon name="insert_drive_file" class="mr-2 material-symbols-outlined" />

              <span
                class="mt-1 download cursor-pointer"
                @click="onDownloadDocument(upload.id, upload.name)"
                v-text="upload.name"
              />
            </div>
            <div v-if="upload.size" class="col-2 mt-1" v-text="`${upload.size} KB`" />
            <div class="col-fixed">
              <Icon name="close" class="cursor-pointer" @click="removeFile(index)" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="p-dialog-footer new-event-modal-footer">
      <AppButton
        v-if="eventToEditSelected && !selectedEventTypeIs([EVENT_TYPE.CASELOAD_MANAGEMENT])"
        type="secondary"
        text="Document"
        icon-left="edit_note"
        @click="emitDocumentEvent"
      />
      <div class="flex justify-content-end w-full">
        <AppButton type="secondary" text="Cancel" @click="emitCloseModal" />

        <AppButton
          v-if="isRecurringEventEdit"
          type="primary"
          text="Save"
          :disabled="v$.$invalid || showWarning || showCMWarning || overlappingError"
          @click="confirmEventOpen = true"
        />
        <AppButton
          v-else
          type="primary"
          text="Save"
          :disabled="v$.$invalid || showWarning || showCMWarning || overlappingError"
          @click="emitSaveNewEvent"
        />
      </div>
    </div>

    <div>
      <ConfirmAction
        v-if="confirmEventOpen"
        header="Edit Recurring Event"
        accept-label="Save"
        @accept="emitSaveNewEvent"
        @cancel="onCancelConfirm"
      >
        <div class="flex flex-column font-bold" :style="'width:350px'">
          <div class="mb-2">
            <RadioButton v-model="editEventConditions" input-id="editThis" value="this" />
            <label for="editThis" class="ml-2">This event</label>
          </div>
          <div>
            <RadioButton v-model="editEventConditions" input-id="editAll" value="future" />
            <label for="editAll" class="ml-2">This and following events</label>
          </div>
        </div>
      </ConfirmAction>
    </div>
  </div>
</template>

<style scoped lang="scss">
.download {
  color: var(--primary-700);
}

.group-icon {
  color: #40b3dd;
}

.new-event-modal {
  min-height: 640px;
  max-width: 640px;
  min-width: 640px;
  display: flex;
  flex-direction: column;

  .event-type__button {
    color: $grayDark;
    background: none;

    &.selected {
      background-color: var(--primary-color-light) !important;
      color: var(--primary-color-dark);
    }
  }

  .p-dialog-header {
    padding: 1.5rem 1.5rem 0rem 1.5rem;

    :deep(.trash-can-icon) {
      color: var(--primary-color-medium);
    }
  }

  .p-dialog-content {
    padding: 0rem;
    flex: 1;

    @media (max-width: 800px) {
      min-width: unset;
      width: calc(100vw - 2rem);
    }

    .new-event-modal-content {
      padding: 1.5rem 1.5rem 0.5rem 1.5rem;

      input,
      .p-inputwrapper {
        border-color: $white;
      }

      .p-dropdown {
        border: 2px solid #f1f3f3;
      }

      .new-event-date-time {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        .new-event-date-time-section {
          .date-picker-icon {
            color: var(--primary-color-medium);
          }
        }
        .new-event-date {
          width: 190px;
          margin-right: 0.5rem;

          :deep(.p-inputtext) {
            font-size: 0.875rem !important;
          }
        }

        .new-event-time {
          width: 85px;

          :deep(.p-inputtext) {
            font-size: 0.875rem !important;
          }
        }

        .new-event-separator {
          margin: 0rem 0.5rem;
          font-weight: bold;
          color: $grayDark;
        }

        .event-recurring-dropdown {
          background: none;
          border: none;
          margin-left: 2.5rem;

          :deep(.p-inputtext) {
            font-weight: bold;
            color: $gray;
            padding-right: 0px;
          }
        }
      }

      .discipline-selector {
        .discipline-icon {
          color: #7e78d2;
        }

        #disciplines__dropdown {
          border: 2px solid $grayLight;
          border-radius: 6px;
        }
      }

      .custom-recurrence__container {
        margin-left: 2.5rem;

        .p-inputnumber {
          :deep(.p-inputtext) {
            width: 3rem !important;
          }
        }

        #interval {
          width: 44px;
        }
      }
    }

    .new-event-event-type {
      padding: 1.5rem;
      background: $white;

      .new-event-tab-menu {
        margin-top: -68px;

        .tab-menu-container {
          margin-top: 0.5rem;
        }
      }
    }

    .field {
      margin-top: 1rem;
      display: flex;
      flex-direction: column;

      .p-inputwrapper {
        width: 100%;
      }
    }

    .p-fileupload {
      :deep(.p-button) {
        .p-button-label,
        .p-button-icon {
          color: $green;
        }

        .p-button-icon {
          font-size: 12px;
        }
      }
    }
  }

  .p-dialog-footer {
    display: flex;
    justify-content: space-between;
  }

  .session-type-info {
    font-size: 14px !important;
    color: var(--gray-700);
  }

  .p-message-icon-position-customized {
    :deep(.p-message-wrapper) {
      display: table;

      .p-message-text,
      .p-message-icon {
        display: table-cell;
      }

      .p-message-icon::before {
        margin-left: 5px;
        margin-top: auto;
      }
    }
  }
}
</style>
